import React from 'react';
import BrandLink from '@/components/ui/BrandLink';
import Tagline from '@/components/ui/Tagline';
import { motion } from 'framer-motion';
import { Cookie, Check, X, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import CookieSettingsLink from '@/components/cookies/CookieSettingsLink';

export default function Cookies() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <BrandLink />
          <a href="/" className="text-sm text-slate-500 hover:text-slate-700">
            Retour à l'accueil
          </a>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8 md:p-12"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-amber-100 p-3 rounded-xl">
              <Cookie className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                Politique Cookies
              </h1>
              <p className="text-slate-500 mt-1">
                Dernière mise à jour : 28 décembre 2025
              </p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none">
            {/* Introduction */}
            <section className="mb-8">
              <p className="text-slate-700 leading-relaxed mb-4">
                Cette page explique comment TrésoPilot utilise des cookies et autres technologies de suivi sur son site web 
                et son application. Conformément au RGPD et à la directive ePrivacy, nous vous informons de vos droits et des 
                moyens de contrôler l'utilisation des cookies.
              </p>
            </section>

            {/* Qu'est-ce qu'un cookie */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Qu'est-ce qu'un cookie ?</h2>
              <p className="text-slate-700 leading-relaxed mb-3">
                Un cookie est un petit fichier texte déposé sur votre appareil (ordinateur, smartphone, tablette) lors de la 
                consultation d'un site web. Il permet de stocker des informations relatives à votre navigation et de vous 
                reconnaître lors de vos visites ultérieures.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Les cookies peuvent être déposés par TrésoPilot (cookies « first party ») ou par des tiers comme nos 
                partenaires d'analyse (cookies « third party »).
              </p>
            </section>

            {/* Types de cookies */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Types de cookies utilisés</h2>
              
              {/* Cookies nécessaires */}
              <div className="bg-green-50 rounded-xl p-6 mb-4 border border-green-200">
                <div className="flex items-center gap-2 mb-3">
                  <Check className="w-5 h-5 text-green-600" />
                  <h3 className="text-lg font-bold text-slate-900 m-0">1. Cookies strictement nécessaires</h3>
                </div>
                <p className="text-slate-700 leading-relaxed mb-3">
                  Ces cookies sont indispensables au bon fonctionnement du Service. Ils ne peuvent pas être désactivés 
                  sans compromettre votre expérience utilisateur.
                </p>
                <p className="text-slate-600 font-semibold mb-2">Finalités :</p>
                <ul className="space-y-1 text-slate-700 list-disc pl-6 mb-3">
                  <li>Authentification et maintien de votre session</li>
                  <li>Sécurisation des transactions et prévention des attaques</li>
                  <li>Mémorisation de vos préférences de langue et de configuration</li>
                  <li>Fonctionnement des formulaires et navigation</li>
                </ul>
                <p className="text-slate-600 text-sm">
                  <strong>Base légale :</strong> Intérêt légitime / Nécessité technique<br />
                  <strong>Durée :</strong> Session ou jusqu'à 12 mois selon le type
                </p>
              </div>

              {/* Cookies analytiques */}
              <div className="bg-blue-50 rounded-xl p-6 mb-4 border border-blue-200">
                <div className="flex items-center gap-2 mb-3">
                  <Settings className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-bold text-slate-900 m-0">2. Cookies analytiques / de performance</h3>
                </div>
                <p className="text-slate-700 leading-relaxed mb-3">
                  Ces cookies nous permettent de comprendre comment les utilisateurs interagissent avec le Service, 
                  d'identifier les problèmes techniques et d'améliorer l'expérience globale.
                </p>
                <p className="text-slate-600 font-semibold mb-2">Finalités :</p>
                <ul className="space-y-1 text-slate-700 list-disc pl-6 mb-3">
                  <li>Mesure d'audience : pages vues, durée de session, taux de rebond</li>
                  <li>Analyse des parcours utilisateurs et des fonctionnalités les plus utilisées</li>
                  <li>Détection d'erreurs techniques et amélioration de la performance</li>
                </ul>
                <p className="text-slate-600 text-sm mb-3">
                  <strong>Outils utilisés :</strong> Outils d'analyse d'audience (si activés)<br />
                  <strong>Base légale :</strong> Consentement<br />
                  <strong>Durée :</strong> Jusqu'à 13 mois
                </p>
                <div className="bg-blue-100 rounded-lg p-3">
                  <p className="text-blue-900 text-sm">
                    <strong>⚙️ Vous pouvez refuser ces cookies</strong> sans impact sur l'accès au Service. 
                    Votre choix est respecté via notre bandeau de consentement.
                  </p>
                </div>
              </div>

              {/* Cookies marketing */}
              <div className="bg-purple-50 rounded-xl p-6 mb-4 border border-purple-200">
                <div className="flex items-center gap-2 mb-3">
                  <Settings className="w-5 h-5 text-purple-600" />
                  <h3 className="text-lg font-bold text-slate-900 m-0">3. Cookies marketing / publicitaires</h3>
                </div>
                <p className="text-slate-700 leading-relaxed mb-3">
                  Ces cookies permettent de personnaliser les contenus publicitaires et de mesurer l'efficacité de nos campagnes 
                  marketing (si applicable).
                </p>
                <p className="text-slate-600 font-semibold mb-2">Finalités :</p>
                <ul className="space-y-1 text-slate-700 list-disc pl-6 mb-3">
                  <li>Affichage de publicités ciblées selon vos centres d'intérêt</li>
                  <li>Suivi des conversions et mesure du ROI marketing</li>
                  <li>Retargeting et personnalisation des campagnes</li>
                </ul>
                <p className="text-slate-600 text-sm mb-3">
                  <strong>Outils utilisés :</strong> Outils de marketing digital (si activés)<br />
                  <strong>Base légale :</strong> Consentement<br />
                  <strong>Durée :</strong> Jusqu'à 13 mois
                </p>
                <div className="bg-purple-100 rounded-lg p-3">
                  <p className="text-purple-900 text-sm">
                    <strong>⚙️ Vous pouvez refuser ces cookies</strong> sans impact sur l'accès au Service. 
                    Votre choix est respecté via notre bandeau de consentement.
                  </p>
                </div>
              </div>
            </section>

            {/* Gestion des cookies */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Comment gérer vos préférences ?</h2>
              
              <h3 className="font-bold text-slate-800 mb-2 mt-4">Via notre bandeau de consentement</h3>
              <p className="text-slate-700 leading-relaxed mb-3">
                Lors de votre première visite, un bandeau vous informe de l'utilisation des cookies et vous permet 
                d'accepter ou de refuser les cookies non strictement nécessaires.
              </p>
              <div className="bg-emerald-50 rounded-xl p-4 mb-4">
                <p className="text-emerald-900 font-semibold mb-2">Modifier vos préférences</p>
                <p className="text-emerald-800 mb-3">
                  Vous pouvez modifier vos choix à tout moment en cliquant sur le bouton ci-dessous :
                </p>
                <CookieSettingsLink variant="button" className="bg-emerald-600 hover:bg-emerald-700" />
              </div>

              <h3 className="font-bold text-slate-800 mb-2 mt-6">Via votre navigateur</h3>
              <p className="text-slate-700 leading-relaxed mb-3">
                Vous pouvez également configurer votre navigateur pour bloquer ou supprimer les cookies. Voici les liens 
                vers les guides des principaux navigateurs :
              </p>
              <ul className="space-y-2 text-slate-700 list-disc pl-6">
                <li><strong>Google Chrome :</strong> Paramètres → Confidentialité et sécurité → Cookies</li>
                <li><strong>Mozilla Firefox :</strong> Paramètres → Vie privée et sécurité → Cookies</li>
                <li><strong>Safari :</strong> Préférences → Confidentialité → Cookies et données de sites web</li>
                <li><strong>Microsoft Edge :</strong> Paramètres → Confidentialité → Cookies</li>
              </ul>
              <p className="text-slate-600 text-sm mt-3 italic">
                ⚠️ Attention : La désactivation de tous les cookies peut affecter le fonctionnement du Service 
                (impossibilité de vous connecter, perte de vos préférences, etc.).
              </p>
            </section>

            {/* Impact du refus */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Conséquences du refus des cookies</h2>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3 bg-slate-50 rounded-lg p-4">
                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-900">Cookies strictement nécessaires</p>
                    <p className="text-slate-700 text-sm">
                      Ne peuvent pas être refusés sans compromettre l'accès au Service.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-slate-50 rounded-lg p-4">
                  <X className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-900">Cookies analytiques</p>
                    <p className="text-slate-700 text-sm">
                      Aucun impact sur l'accès au Service. Nous ne pourrons simplement pas améliorer l'expérience 
                      utilisateur de manière aussi efficace.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-slate-50 rounded-lg p-4">
                  <X className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-900">Cookies marketing</p>
                    <p className="text-slate-700 text-sm">
                      Aucun impact sur l'accès au Service. Vous verrez simplement des publicités moins pertinentes 
                      (si applicable).
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Durées de conservation */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Durées de conservation</h2>
              <p className="text-slate-700 leading-relaxed mb-3">
                Conformément aux recommandations de la CNIL, les cookies soumis au consentement sont conservés pour une 
                durée maximale de <strong>13 mois</strong> à compter de leur dépôt sur votre terminal.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Votre consentement est valable pour une durée maximale de 13 mois. Au-delà, nous vous redemanderons 
                votre accord.
              </p>
            </section>

            {/* Plus d'informations */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Plus d'informations</h2>
              <p className="text-slate-700 leading-relaxed mb-3">
                Pour en savoir plus sur les cookies et leur fonctionnement, vous pouvez consulter :
              </p>
              <ul className="space-y-2 text-slate-700 list-disc pl-6">
                <li><strong>CNIL :</strong> www.cnil.fr/fr/cookies-et-autres-traceurs</li>
                <li><strong>AboutCookies.org :</strong> www.aboutcookies.org</li>
                <li><strong>AllAboutCookies.org :</strong> www.allaboutcookies.org</li>
              </ul>
            </section>

            {/* Contact */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Questions ?</h2>
              <p className="text-slate-700 leading-relaxed mb-3">
                Pour toute question concernant l'utilisation des cookies sur TrésoPilot, contactez-nous à :
              </p>
              <div className="bg-emerald-50 rounded-xl p-4">
                <a href="mailto:contact@tresopilot.fr" className="text-emerald-600 hover:text-emerald-700 font-semibold">
                  contact@tresopilot.fr
                </a>
              </div>
            </section>

            {/* Modifications */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Modifications</h2>
              <p className="text-slate-700 leading-relaxed">
                Cette politique peut être mise à jour à tout moment pour refléter les évolutions de nos pratiques ou de la 
                réglementation. La date de dernière mise à jour est indiquée en haut de cette page.
              </p>
            </section>

            <div className="border-t border-slate-200 pt-6 mt-8 text-center text-slate-500 text-sm space-y-2">
              <p><Tagline /></p>
              <p>© {new Date().getFullYear()} TrésoPilot. Tous droits réservés.</p>
              <p className="mt-2">
                Document légal — Dernière mise à jour : 28 décembre 2025
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}